import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './authApi';

export const customerApi = createApi({
  reducerPath: 'customerApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Customer'],
  endpoints: (builder) => ({

    searchCustomers: builder.query({
      query: ({ search = '', page = 1, per_page = 20 } = {}) => ({
        url: '/customers',
        method: 'GET',
        params: { search, page, per_page },
      }),
      providesTags: [{ type: 'Customer', id: 'LIST' }],
    }),

    getCustomer: builder.query({
      query: (id) => `/customers/${id}`,
      providesTags: (result, error, id) => [{ type: 'Customer', id }],
    }),

    createCustomer: builder.mutation({
      query: (customerData) => ({
        url: '/customers',
        method: 'POST',
        body: { customer: customerData },
      }),
      invalidatesTags: [{ type: 'Customer', id: 'LIST' }],
    }),

    // Manual loyalty enrollment (awards the signup bonus). Used when the org
    // does not auto-enroll. Returns the customer with a refreshed `loyalty` summary.
    enrollLoyalty: builder.mutation({
      query: (id) => ({
        url: `/customers/${id}/enroll_loyalty`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Customer', id }],
    }),

    // Reactivate a deactivated customer at the counter. A returning inactive
    // customer is otherwise a dead end — the order guard blocks inactive ones —
    // so the cashier reactivates before attaching them to the bill.
    activateCustomer: builder.mutation({
      query: (id) => ({
        url: `/customers/${id}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Customer', id }, { type: 'Customer', id: 'LIST' }],
    }),

    // Wallet passbooks for the counter — where store credit / points came
    // from (credit notes, earned on bills) and went (bills paid with them).
    getStoreCreditTransactions: builder.query({
      query: ({ id, page = 1, per_page = 20 }) => ({
        url: `/customers/${id}/store_credit_transactions`,
        params: { page, per_page },
      }),
    }),

    getLoyaltyTransactions: builder.query({
      query: ({ id, page = 1, per_page = 20 }) => ({
        url: `/customers/${id}/loyalty_transactions`,
        params: { page, per_page },
      }),
    }),

  }),
});

export const {
  useSearchCustomersQuery,
  useLazySearchCustomersQuery,
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useEnrollLoyaltyMutation,
  useActivateCustomerMutation,
  useGetStoreCreditTransactionsQuery,
  useGetLoyaltyTransactionsQuery,
} = customerApi;
